import type {
  ComposerAttachment,
  ComposerAttachmentUpload,
  ComposerImage,
} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {repositoryURL, type Repository} from '@github-ui/agent-sessions-interface/components/repo-picker/repo'
import {isRepositoryInScope} from '@github-ui/agent-sessions-interface/components/repo-picker/scope'
import type {
  MessageAttachment,
  MessageAttachmentKind,
  MessageEmbeddedResourceAttachment,
} from '@microsoft/agent-host-protocol'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ClipboardEvent,
} from 'react'

import {
  classifyImageSupport,
  classifyPasteRejection,
  isAllowedImage,
  MAX_INLINE_ATTACHMENTS,
  processUploadedFiles,
  type PendingUpload,
  type RejectedFile,
  type TrackedAttachment,
} from '../attachments/process-uploaded-files'
import {checkUploadBudget} from '../attachments/upload-to-sandbox'
import {isRetriableWriteFailure, ResourceWriteError} from '@github-ui/ahp-relay/resource-write'
import type {SandboxUploadHandle} from './use-sandbox-upload'
import {implicitAttachmentIcon, implicitAttachmentKey} from '../attachments/attachment-refs'
import {readImageFile} from '../attachments/read-image-file'
import {useAttachmentRejections} from './use-attachment-rejections'
import {isAttachmentInScope} from '../attachments/attachment-scope'
import {
  createRepositoryFileAttachment,
  repositoryFileAttachmentKey,
  type RepositoryFileSelection,
} from '../attachments/repository-file-attachment'

const SIMPLE_ATTACHMENT = 'simple' as MessageAttachmentKind.Simple
/**
 * How long an upload must run before its progress is worth showing. Short enough that a
 * genuinely slow upload never feels unacknowledged, long enough that attaching a
 * screenshot shows no chrome at all.
 */
const PROGRESS_REVEAL_DELAY_MS = 600

const RESOURCE_ATTACHMENT = 'resource' as MessageAttachmentKind.Resource
const REPO_ATTACHMENT_PREFIX = 'repo-attachment:'
const PASTED_IMAGE_LABEL = 'Pasted Image'

function mergeTrackedAttachments(existing: TrackedAttachment[], added: TrackedAttachment[], explicitSelection = false) {
  const key = (item: TrackedAttachment) => repositoryFileAttachmentKey(item.ahp) ?? `token:${item.token.id}`
  const result = [...existing]
  const indices = new Map(result.map((item, index) => [key(item), index]))
  for (const item of added) {
    const identity = key(item)
    const index = indices.get(identity)
    if (index === undefined) {
      indices.set(identity, result.length)
      result.push(item)
    } else if (explicitSelection && repositoryFileAttachmentKey(item.ahp) !== undefined) {
      result[index] = item
    }
  }
  return result
}

function isTrackedAttachmentInScope(item: TrackedAttachment, scopeOwner: string | null | undefined): boolean {
  return isAttachmentInScope(item.ahp, scopeOwner)
}

function isPastedImageName(name: string): boolean {
  return name === PASTED_IMAGE_LABEL || name.startsWith(`${PASTED_IMAGE_LABEL} `)
}

function renumberPastedImages(images: ComposerImage[]): ComposerImage[] {
  let pastedImageCount = 0
  return images.map(image => {
    if (!isPastedImageName(image.name)) return image
    pastedImageCount += 1
    const name = pastedImageCount === 1 ? PASTED_IMAGE_LABEL : `${PASTED_IMAGE_LABEL} ${pastedImageCount}`
    return image.name === name ? image : {...image, name}
  })
}

function imageAttachment(image: ComposerImage): MessageAttachment {
  return {
    type: 'embeddedResource',
    label: image.name,
    data: image.data,
    contentType: image.contentType,
    displayKind: 'image',
    _meta: {'github.attachmentId': image.id},
  } as MessageEmbeddedResourceAttachment
}

function repositoryAttachment(repository: Repository): MessageAttachment {
  const [owner, ...rest] = repository.name.split('/')
  return {
    type: SIMPLE_ATTACHMENT,
    label: repository.name,
    displayKind: 'repository',
    _meta: {
      'copilot.attachmentDetail': {
        type: 'github_repository',
        repo: {owner: owner ?? '', name: rest.join('/'), id: repository.id},
        url: repositoryURL(repository),
      },
    },
  }
}

export type ComposerFileSelection = RepositoryFileSelection

export interface ComposerReferenceController {
  pastedReferences: TrackedAttachment[]
  handlePaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  removePastedReference: (id: string) => void
  clearPastedReferences: (ids: string[]) => void
  selectSubmittedPastedReferences: (submitted: TrackedAttachment[]) => TrackedAttachment[]
  reconcileFromText?: (text: string) => void
}

export interface ComposerAttachmentsSnapshot {
  attachments: MessageAttachment[]
  repos: Repository[]
  images: ComposerImage[]
  files: TrackedAttachment[]
  references: TrackedAttachment[]
  clear: () => void
}

export function collectExplicitAttachmentKeys(
  repos: Repository[],
  trackedItems: TrackedAttachment[],
  injectedItems: Array<{ahp: MessageAttachment}>,
  pastedItems: TrackedAttachment[],
): Set<string> {
  const keys = new Set<string>()
  for (const repo of repos) keys.add(repositoryURL(repo))
  for (const item of trackedItems) keys.add(implicitAttachmentKey(item.ahp))
  for (const item of injectedItems) keys.add(implicitAttachmentKey(item.ahp))
  for (const item of pastedItems) keys.add(implicitAttachmentKey(item.ahp))
  return keys
}

export interface ComposerAttachmentsController {
  attachments: ComposerAttachment[]
  repoChips: ComposerAttachment[]
  trackedChips: ComposerAttachment[]
  pastedChips: ComposerAttachment[]
  tracked: TrackedAttachment[]
  pastedReferences: TrackedAttachment[]
  images: ComposerImage[]
  hasAttachments: boolean
  /**
   * @deprecated Names only, with no reason and a 5s auto-dismiss. Read `rejections` or
   * `rejectionMessages` instead, which carry why each file was refused and stay put until the user
   * dismisses them. Kept for surfaces that still render the older generic sentence.
   */
  skippedFiles: string[]
  rejections: readonly RejectedFile[]
  rejectionMessages: string[]
  dismissRejections: () => void
  isUploading: boolean
  /**
   * Whether a file is still being written to the sandbox. Surfaces block submit on this
   * so a turn never references an upload that has not landed on disk yet.
   */
  uploadsInFlight: boolean
  handleFilesAdded: (files: File[], source: 'paste' | 'drop') => Promise<void>
  handleFileInputChange: (event: ChangeEvent<HTMLInputElement>) => Promise<void>
  handleFilePickerSelect: (selections: readonly ComposerFileSelection[]) => void
  addTrackedAttachments: (items: TrackedAttachment[], options?: {replacePrefix?: string}) => void
  removeTrackedAttachmentsByPrefix: (prefix: string) => void
  handleUpdateRepos: (repos: Repository[]) => void
  handleRemoveAttachment: (id: string) => void
  handlePaste: (event: ClipboardEvent<HTMLTextAreaElement>) => void
  reconcileFromText: (text: string) => void
  openFilePicker: () => void
  closeFilePicker: () => void
  filePickerOpen: boolean
  openRepoPicker: () => void
  closeRepoPicker: () => void
  repoPickerOpen: boolean
  selectedRepos: Repository[]
  previewImage: ComposerImage | null
  setPreviewImage: (image: ComposerImage | null) => void
  removeImage: (id: string) => void
  snapshot: () => ComposerAttachmentsSnapshot
}

export interface UseComposerAttachmentsOptions {
  onAttach?: () => void
  inlineImages?: boolean
  references?: ComposerReferenceController
  /**
   * Element the rejection announcement is anchored to. `ariaNotify` skips uninteresting elements,
   * so this should be the composer textarea rather than a wrapper.
   */
  announceAnchorRef?: React.RefObject<HTMLElement | null>
  /** The rendered rejection banner, so focus can leave it before a new attempt removes it. */
  rejectionBannerRef?: React.RefObject<HTMLElement | null>
  focusComposer?: () => void
  /** Mount-scoped prepared attachments to display and preserve until the first submit. */
  initialAttachments?: readonly MessageAttachment[]
  /**
   * Active scope owner. When set, repository and file attachments owned by any
   * other account are pruned on change and blocked at selection/submission, so
   * an out-of-scope attachment can never ride along on a scoped turn.
   */
  scopeOwner?: string | null
  /**
   * Writes attachments into the session's sandbox filesystem. When omitted or
   * unavailable, attachments are inlined exactly as they were before sandbox
   * uploads existed.
   */
  sandboxUpload?: SandboxUploadHandle
}

/** A file being written to, or already written to, the sandbox filesystem. */
interface UploadEntry {
  id: string
  name: string
  /**
   * Whether this upload owns a composer token. False when the file is also inlined,
   * because the inline image/document chip already represents it.
   */
  visible: boolean
  /** Used to tell an inlined image, which has no chip, from an inlined document, which has one. */
  kind: PendingUpload['kind']
  status: 'uploading' | 'failed' | 'done'
  /**
   * Whether progress is worth showing. Most uploads finish in well under a second,
   * so a chip that appears and vanishes is just noise. Failures ignore this.
   */
  revealed: boolean
  /** 0-100, tracked only while uploading. */
  percent: number
  error?: string
  retriable?: boolean
  /** The resource attachment handed to the agent. Set once the write completes. */
  ahp?: MessageAttachment
}

function initialAttachmentState(attachments: readonly MessageAttachment[]) {
  const tracked: TrackedAttachment[] = []
  const images: ComposerImage[] = []

  for (const [index, attachment] of attachments.entries()) {
    if (
      attachment.type === 'embeddedResource' &&
      attachment.displayKind === 'image' &&
      typeof attachment.data === 'string' &&
      typeof attachment.contentType === 'string'
    ) {
      const attachmentId = attachment._meta?.['github.attachmentId']
      const id =
        typeof attachmentId === 'string' && attachmentId.trim() ? attachmentId.trim() : `prepared-image-${index}`
      images.push({
        id,
        name: attachment.label,
        data: attachment.data,
        contentType: attachment.contentType,
        dataUrl: `data:${attachment.contentType};base64,${attachment.data}`,
      })
      continue
    }

    tracked.push({
      token: {
        id: `prepared-attachment-${index}-${implicitAttachmentKey(attachment)}`,
        label: attachment.label,
        icon: implicitAttachmentIcon(attachment),
      },
      ahp: attachment,
    })
  }

  return {tracked, images}
}

async function addInputFiles(
  event: ChangeEvent<HTMLInputElement>,
  addFiles: ComposerAttachmentsController['handleFilesAdded'],
) {
  const files = event.target.files
  const input = event.currentTarget
  if (!files) return

  try {
    await addFiles(Array.from(files), 'drop')
  } finally {
    input.value = ''
  }
}

/**
 * Owns the common file, image, repository, and reference draft state used by
 * established AHP composers. Product surfaces provide reference resolution and
 * picker UI, while this controller produces one normalized attachment snapshot
 * and clears only the items accepted by the runtime.
 */
export function useComposerAttachments({
  onAttach,
  inlineImages = true,
  references,
  announceAnchorRef,
  rejectionBannerRef,
  focusComposer,
  initialAttachments = [],
  scopeOwner,
  sandboxUpload,
}: UseComposerAttachmentsOptions = {}): ComposerAttachmentsController {
  const [initialState] = useState(() => initialAttachmentState(initialAttachments))
  const [tracked, setTracked] = useState<TrackedAttachment[]>(initialState.tracked)
  const [images, setImages] = useState<ComposerImage[]>(initialState.images)
  const [selectedRepos, setSelectedRepos] = useState<Repository[]>([])
  const [skippedFiles, setSkippedFiles] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [previewImage, setPreviewImage] = useState<ComposerImage | null>(null)
  const [filePickerOpen, setFilePickerOpen] = useState(false)
  const [repoPickerOpen, setRepoPickerOpen] = useState(false)
  const [uploadEntries, setUploadEntries] = useState<UploadEntry[]>([])
  const inlineAttachmentCountRef = useRef(0)
  const pendingUploadCountRef = useRef(0)
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())
  const scopeRevision = useMemo(() => ({owner: scopeOwner}), [scopeOwner])
  const activeScopeRef = useRef(scopeRevision)
  useLayoutEffect(() => {
    activeScopeRef.current = scopeRevision
  }, [scopeRevision])
  const {
    rejections,
    reportRejections,
    beginAttempt,
    clearRejections,
    snapshotRejections,
    messages: rejectionMessages,
  } = useAttachmentRejections(announceAnchorRef, rejectionBannerRef, focusComposer)

  useEffect(() => {
    inlineAttachmentCountRef.current = tracked.filter(item => item.ahp.type === 'embeddedResource').length
  }, [tracked])

  // When the active scope owner changes, drop any repository/file attachments the
  // new scope excludes. The scoped loader only filters what the picker offers;
  // already-selected attachments (or ones a stale in-flight request repopulated)
  // must be pruned here so they cannot be submitted on the scoped turn. This runs
  // during render (React's "adjusting state when a prop changes" pattern) to avoid
  // a cascading-render setState-in-effect.
  const [previousScopeOwner, setPreviousScopeOwner] = useState(scopeOwner)
  if (scopeOwner !== previousScopeOwner) {
    setPreviousScopeOwner(scopeOwner)
    setFilePickerOpen(false)
    setRepoPickerOpen(false)
    if (scopeOwner !== undefined) {
      setSelectedRepos(current => {
        const next = current.filter(repo => isRepositoryInScope(repo, scopeOwner))
        return next.length === current.length ? current : next
      })
      setTracked(current => {
        const next = current.filter(item => isTrackedAttachmentInScope(item, scopeOwner))
        return next.length === current.length ? current : next
      })
    }
  }

  const warm = useCallback(() => onAttach?.(), [onAttach])

  // Bytes and file count already committed this turn, so the per-turn budget survives
  // several separate drops. Reset when the composer is cleared on submit.
  const uploadBudgetRef = useRef({files: 0, bytes: 0})
  const pendingUploadsRef = useRef(new Map<string, PendingUpload>())
  const uploadAbortRef = useRef(new Map<string, AbortController>())
  /**
   * URIs of uploads that finished writing, so a later removal can delete the file.
   * Aborting is enough while a write is in flight, but once it completes there is
   * nothing left to abort and only an explicit delete clears the bytes.
   */
  const writtenUriRef = useRef(new Map<string, string>())

  const updateUploadEntry = useCallback((id: string, patch: Partial<UploadEntry>) => {
    setUploadEntries(prev => prev.map(entry => (entry.id === id ? {...entry, ...patch} : entry)))
  }, [])

  /**
   * Deletes a file that already landed on disk. Best effort: the attachment is dropped
   * from the composer either way, and a failed delete must not block removal.
   */
  const removeWrittenFile = useCallback(
    async (uri: string) => {
      try {
        await sandboxUpload?.remove(uri)
      } catch {
        // Nothing actionable: the user asked to remove an attachment, not to manage the
        // sandbox, so a failure here is not worth surfacing.
      }
    },
    [sandboxUpload],
  )

  /**
   * Forgets an upload entirely: aborts it if still running, deletes it if it already
   * landed, releases its share of the turn budget, and drops its record so `snapshot()`
   * cannot emit a `resource` for a file the user removed.
   *
   * Runs for completed uploads too. Those are already gone from the in-flight maps, so
   * guarding on those maps would leave the finished entry — and its path — attached.
   */
  const discardUpload = useCallback(
    (id: string) => {
      const pending = pendingUploadsRef.current.get(id)
      uploadAbortRef.current.get(id)?.abort()
      uploadAbortRef.current.delete(id)
      pendingUploadsRef.current.delete(id)
      const writtenUri = writtenUriRef.current.get(id)
      writtenUriRef.current.delete(id)
      if (writtenUri) void removeWrittenFile(writtenUri)
      if (pending) {
        uploadBudgetRef.current = {
          files: Math.max(0, uploadBudgetRef.current.files - 1),
          bytes: Math.max(0, uploadBudgetRef.current.bytes - pending.file.size),
        }
      }
      setUploadEntries(prev => prev.filter(entry => entry.id !== id))
    },
    [removeWrittenFile],
  )

  /**
   * Set when the host answers `unsupported`, which means this build of the host has no
   * `resourceWrite` at all. Availability is otherwise optimistic, so without this every
   * later file would be accepted for a disk write that can never succeed.
   */
  const [sandboxUnsupported, setSandboxUnsupported] = useState(false)
  const sandboxAvailable = (sandboxUpload?.available ?? false) && !sandboxUnsupported

  const runUpload = useCallback(
    async (pending: PendingUpload) => {
      if (!sandboxAvailable || !sandboxUpload) return
      // The file may have been removed while this was waiting its turn in the queue.
      // Writing it now would put an orphan on disk that nothing references.
      if (!pendingUploadsRef.current.has(pending.id)) return
      const controller = new AbortController()
      uploadAbortRef.current.set(pending.id, controller)
      updateUploadEntry(pending.id, {status: 'uploading', revealed: false, percent: 0, error: undefined})
      const revealTimer = setTimeout(() => updateUploadEntry(pending.id, {revealed: true}), PROGRESS_REVEAL_DELAY_MS)
      try {
        const uploaded = await sandboxUpload.upload({
          file: pending.file,
          fallbackName: `upload-${pending.id}`,
          signal: controller.signal,
          onProgress: progress => {
            updateUploadEntry(pending.id, {
              percent:
                progress.totalBytes === 0 ? 100 : Math.round((progress.uploadedBytes / progress.totalBytes) * 100),
            })
          },
        })
        pendingUploadsRef.current.delete(pending.id)
        writtenUriRef.current.set(pending.id, uploaded.uri)
        updateUploadEntry(pending.id, {
          status: 'done',
          percent: 100,
          error: undefined,
          retriable: undefined,
          ahp: {
            type: RESOURCE_ATTACHMENT,
            label: uploaded.filename,
            uri: uploaded.uri,
            sizeHint: uploaded.bytes,
            contentType: uploaded.contentType,
          },
        })
      } catch (error) {
        if (controller.signal.aborted) {
          setUploadEntries(prev => prev.filter(entry => entry.id !== pending.id))
          pendingUploadsRef.current.delete(pending.id)
          return
        }
        // An `unsupported` failure is about the host, not this file: retrying it or
        // accepting further disk-only files would fail identically. Stop routing to the
        // sandbox so those files are rejected up front with a reason the user can act on.
        if (error instanceof ResourceWriteError && error.kind === 'unsupported') setSandboxUnsupported(true)
        // The bytes never landed, so give the budget back rather than charging the
        // user for a write the sandbox refused.
        uploadBudgetRef.current.bytes -= pending.file.size
        uploadBudgetRef.current.files -= 1
        updateUploadEntry(pending.id, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'Upload failed',
          retriable: isRetriableWriteFailure(error),
        })
      } finally {
        clearTimeout(revealTimer)
        uploadAbortRef.current.delete(pending.id)
      }
    },
    [sandboxAvailable, sandboxUpload, updateUploadEntry],
  )

  /**
   * Every upload — first attempt or retry — goes through this one chain, so only one
   * write is ever in flight. The relay is a single socket, so interleaving writes adds
   * no throughput while pushing every chunk group closer to its reassembly timeout, and
   * a retry starting alongside a fresh drop would break that guarantee.
   */
  const sandboxWriteQueueRef = useRef<Promise<void>>(Promise.resolve())

  const enqueueUpload = useCallback(
    (pending: PendingUpload) => {
      const previous = sandboxWriteQueueRef.current
      sandboxWriteQueueRef.current = (async () => {
        await previous
        await runUpload(pending)
      })()
    },
    [runUpload],
  )

  const startUploads = useCallback(
    (uploads: readonly PendingUpload[]) => {
      if (uploads.length === 0) return
      setUploadEntries(prev => [
        ...prev,
        ...uploads.map(pending => ({
          id: pending.id,
          name: pending.file.name,
          visible: !pending.alsoInlined,
          kind: pending.kind,
          status: 'uploading' as const,
          revealed: false,
          percent: 0,
        })),
      ])
      for (const pending of uploads) {
        pendingUploadsRef.current.set(pending.id, pending)
        uploadBudgetRef.current.files += 1
        uploadBudgetRef.current.bytes += pending.file.size
      }
      for (const pending of uploads) enqueueUpload(pending)
    },
    [enqueueUpload],
  )

  const retryUpload = useCallback(
    (id: string) => {
      const pending = pendingUploadsRef.current.get(id)
      if (!pending) return

      // The sandbox may have gone away, and other files may have taken the budget this
      // one gave back when it failed. Re-check both before charging for the retry,
      // otherwise a retry can leak its reservation or exceed the per-turn limits.
      if (!sandboxAvailable) {
        updateUploadEntry(id, {status: 'failed', error: 'The sandbox is no longer available', retriable: false})
        return
      }
      if (checkUploadBudget(pending.file.size, uploadBudgetRef.current)) {
        updateUploadEntry(id, {
          status: 'failed',
          error: 'This turn has reached its upload limit',
          retriable: false,
        })
        return
      }

      uploadBudgetRef.current.files += 1
      uploadBudgetRef.current.bytes += pending.file.size
      updateUploadEntry(id, {status: 'uploading', percent: 0, revealed: false, error: undefined})
      enqueueUpload(pending)
    },
    [enqueueUpload, sandboxAvailable, updateUploadEntry],
  )

  const handleFilesAdded = useCallback(
    async (files: File[], source: 'paste' | 'drop') => {
      beginAttempt()
      pendingUploadCountRef.current += 1
      setIsUploading(true)
      const previousUpload = uploadQueueRef.current
      const nextUpload = Promise.withResolvers<void>()
      uploadQueueRef.current = nextUpload.promise
      const finishUpload = () => {
        nextUpload.resolve()
        pendingUploadCountRef.current -= 1
        setIsUploading(pendingUploadCountRef.current > 0)
      }

      try {
        await previousUpload
        if (inlineImages && source === 'paste') {
          // Paste stays image-only so pasted screenshots keep their "Pasted Image N" labels, but a
          // file it cannot take still has to say so rather than disappear.
          const classified = files.map(file => ({
            file,
            imageSupport: isAllowedImage(file) ? classifyImageSupport(file) : null,
          }))
          const imageFiles = classified.filter(({imageSupport}) => imageSupport === 'accepted').map(({file}) => file)
          const rejected: RejectedFile[] = await Promise.all(
            classified
              .filter(({imageSupport}) => imageSupport !== 'accepted')
              .map(async ({file, imageSupport}) => {
                const reason =
                  imageSupport === 'unsupported-type' || imageSupport === 'empty'
                    ? imageSupport
                    : await classifyPasteRejection(file)
                return {
                  name: file.name,
                  reason:
                    reason === 'paste-image-only' && inlineAttachmentCountRef.current >= MAX_INLINE_ATTACHMENTS
                      ? 'too-many'
                      : reason,
                }
              }),
          )
          const read = await Promise.all(
            imageFiles.map(file => {
              const id = crypto.randomUUID()
              return readImageFile(file, id, id)
            }),
          )
          for (const [index, file] of imageFiles.entries()) {
            if (read[index] === null) rejected.push({name: file.name, reason: 'unreadable'})
          }
          reportRejections(rejected)
          const added = read.filter((image): image is ComposerImage => image !== null)
          if (added.length > 0) {
            warm()
            setImages(prev =>
              renumberPastedImages([...prev, ...added.map(image => ({...image, name: PASTED_IMAGE_LABEL}))]),
            )
          }
          // A pasted screenshot must reach disk too. Without this it exists only as an
          // inline blob, which is the whole bug: the agent can see the image but has no
          // path to hand to issue creation.
          if (sandboxAvailable) {
            const pasted: PendingUpload[] = []
            for (const [index, file] of imageFiles.entries()) {
              const image = read[index]
              if (!image) continue
              if (checkUploadBudget(file.size, uploadBudgetRef.current)) continue
              pasted.push({id: image.id, file, kind: 'image', alsoInlined: true})
            }
            startUploads(pasted)
          }
        } else {
          const {
            items,
            images: newImages,
            skipped,
            uploads,
          } = await processUploadedFiles(files, {
            currentUploadCount: () => inlineAttachmentCountRef.current,
            createImageId: () => crypto.randomUUID(),
            sandboxAvailable,
            uploadBudgetUsed: uploadBudgetRef.current,
          })
          startUploads(uploads)
          inlineAttachmentCountRef.current += items.length
          if (items.length > 0) setTracked(prev => [...prev, ...items])
          if (inlineImages && newImages.length > 0) setImages(prev => [...prev, ...newImages])
          // A disk-only file (an archive, a video) produces no inline item and no
          // image, so without `uploads` here attaching one would skip the preparation
          // every other accepted attachment triggers.
          if (items.length > 0 || uploads.length > 0 || (inlineImages && newImages.length > 0)) warm()
          reportRejections(skipped)
          if (skipped.length > 0) setSkippedFiles(skipped.map(file => file.name))
        }
      } catch (error) {
        finishUpload()
        return Promise.reject(error)
      }
      finishUpload()
    },
    [beginAttempt, inlineImages, reportRejections, sandboxAvailable, startUploads, warm],
  )

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => addInputFiles(event, handleFilesAdded),
    [handleFilesAdded],
  )

  const handleFilePickerSelect = useCallback(
    (selections: readonly ComposerFileSelection[]) => {
      if (selections.length === 0) return
      // Enforce the scope at selection time so an out-of-scope repository's files
      // cannot be attached even if the picker briefly offered them.
      if (activeScopeRef.current !== scopeRevision) return
      const inScope = selections.filter(selection => isRepositoryInScope(selection.repo, scopeOwner))
      if (inScope.length === 0) return
      const items = inScope.map(selection => createRepositoryFileAttachment(selection))
      warm()
      setTracked(prev => mergeTrackedAttachments(prev, items, true))
    },
    [warm, scopeOwner, scopeRevision],
  )

  const addTrackedAttachments = useCallback(
    (items: TrackedAttachment[], options?: {replacePrefix?: string}) => {
      if (items.length === 0) return
      if (activeScopeRef.current !== scopeRevision) return
      const scoped = items.filter(item => isTrackedAttachmentInScope(item, scopeOwner))
      if (scoped.length === 0) return
      warm()
      const replacePrefix = options?.replacePrefix
      setTracked(prev => {
        const base = replacePrefix ? prev.filter(item => !item.token.id.startsWith(replacePrefix)) : prev
        return mergeTrackedAttachments(base, scoped)
      })
    },
    [warm, scopeOwner, scopeRevision],
  )

  const removeTrackedAttachmentsByPrefix = useCallback((prefix: string) => {
    setTracked(prev => {
      const next = prev.filter(item => !item.token.id.startsWith(prefix))
      return next.length === prev.length ? prev : next
    })
  }, [])

  const handleUpdateRepos = useCallback(
    (repos: Repository[]) => {
      // Enforce the scope at selection time so a stale in-flight picker request
      // cannot repopulate the selection with out-of-scope repositories.
      if (activeScopeRef.current !== scopeRevision) return
      const scoped = repos.filter(repo => isRepositoryInScope(repo, scopeOwner))
      if (scoped.length > 0) warm()
      setSelectedRepos(scoped)
    },
    [warm, scopeOwner, scopeRevision],
  )

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      if (id.startsWith(REPO_ATTACHMENT_PREFIX)) {
        const repoId = Number(id.slice(REPO_ATTACHMENT_PREFIX.length))
        setSelectedRepos(prev => prev.filter(repo => repo.id !== repoId))
      } else if (references?.pastedReferences.some(item => item.token.id === id)) {
        references.removePastedReference(id)
      } else {
        setTracked(prev => prev.filter(item => item.token.id !== id))
        discardUpload(id)
      }
    },
    [references, discardUpload],
  )

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLTextAreaElement>) => {
      references?.handlePaste(event)
    },
    [references],
  )

  const reconcileFromText = useCallback(
    (text: string) => {
      references?.reconcileFromText?.(text)
    },
    [references],
  )

  // Legacy `skippedFiles` behavior only. The typed `rejections` path deliberately has no timer:
  // auto-dismissing imposes a WCAG 2.2.1 time limit on reading a message that can run to several
  // clauses. Rejections clear on dismiss, on submit, or when the next attachment attempt starts.
  useEffect(() => {
    if (skippedFiles.length === 0) return
    const timer = setTimeout(() => setSkippedFiles([]), 5000)
    return () => clearTimeout(timer)
  }, [skippedFiles])

  const repoChips = useMemo(
    () =>
      selectedRepos
        .filter(repo => isRepositoryInScope(repo, scopeOwner))
        .map(repo => ({id: `${REPO_ATTACHMENT_PREFIX}${repo.id}`, label: repo.name, icon: 'repo' as const})),
    [selectedRepos, scopeOwner],
  )
  const trackedChips = useMemo(
    () => tracked.filter(item => isTrackedAttachmentInScope(item, scopeOwner)).map(item => item.token),
    [tracked, scopeOwner],
  )
  const pastedChips = useMemo(
    () =>
      references?.pastedReferences
        .filter(item => isTrackedAttachmentInScope(item, scopeOwner))
        .map(item => item.token) ?? [],
    [references, scopeOwner],
  )
  const uploadStateById = useMemo(
    () =>
      new Map<string, ComposerAttachmentUpload>(
        uploadEntries.flatMap((entry): Array<[string, ComposerAttachmentUpload]> => {
          if (entry.status === 'uploading' && entry.revealed) {
            return [[entry.id, {status: 'uploading', percent: entry.percent}]]
          }
          if (entry.status === 'failed') {
            return [
              [
                entry.id,
                {
                  status: 'failed',
                  error: entry.error,
                  onRetry: entry.retriable ? () => retryUpload(entry.id) : undefined,
                },
              ],
            ]
          }
          return []
        }),
      ),
    [uploadEntries, retryUpload],
  )

  /**
   * A file that is both inlined and written to disk already has a chip, so its upload
   * state is merged onto that chip rather than rendered as a second token for one file.
   */
  const withUploadState = useCallback(
    (chips: ComposerAttachment[]) =>
      chips.map(chip => {
        const upload = uploadStateById.get(chip.id)
        return upload ? {...chip, upload} : chip
      }),
    [uploadStateById],
  )

  const uploadChips = useMemo<ComposerAttachment[]>(
    () =>
      uploadEntries
        // A failed inlined upload has no other way to say so: its image thumbnail shows
        // no upload state, so surface a token the user can read and retry from.
        .filter(entry => entry.visible || (entry.status === 'failed' && entry.kind === 'image'))
        .map(entry => ({
          id: entry.id,
          label: entry.name,
          icon: 'file' as const,
          upload: uploadStateById.get(entry.id),
        })),
    [uploadEntries, uploadStateById],
  )
  const attachments = useMemo(
    () => [...repoChips, ...withUploadState(trackedChips), ...uploadChips, ...withUploadState(pastedChips)],
    [pastedChips, repoChips, trackedChips, uploadChips, withUploadState],
  )
  const hasAttachments = attachments.length > 0 || (inlineImages && images.length > 0)
  const uploadsInFlight = useMemo(() => uploadEntries.some(entry => entry.status === 'uploading'), [uploadEntries])

  const snapshot = useCallback((): ComposerAttachmentsSnapshot => {
    // Final owner guard: even if a scope change and submit race the pruning
    // effect, out-of-scope repositories/files must never reach a scoped turn.
    const submittedFiles = tracked.filter(item => isTrackedAttachmentInScope(item, activeScopeRef.current.owner))
    const submittedRepos = selectedRepos.filter(repo => isRepositoryInScope(repo, activeScopeRef.current.owner))
    const submittedImages = inlineImages ? images : []
    const submittedUploads = uploadEntries.flatMap(entry => (entry.ahp ? [entry.ahp] : []))
    const submittedRejections = snapshotRejections()
    const submittedReferences = references
      ? references
          .selectSubmittedPastedReferences(references.pastedReferences)
          .filter(item => isTrackedAttachmentInScope(item, activeScopeRef.current.owner))
      : []
    const clear = () => {
      const fileIds = new Set(submittedFiles.map(item => item.token.id))
      const repoIds = new Set(submittedRepos.map(repo => repo.id))
      const imageIds = new Set(submittedImages.map(image => image.id))
      setTracked(current => current.filter(item => !fileIds.has(item.token.id)))
      setSelectedRepos(current => current.filter(repo => !repoIds.has(repo.id)))
      setImages(current => current.filter(image => !imageIds.has(image.id)))
      setUploadEntries(current => current.filter(entry => entry.status === 'uploading'))
      // Sent files stay on disk for the agent to read, so forget their URIs: they are no
      // longer removable from the composer and nothing should delete them later.
      for (const entry of uploadEntries) {
        if (entry.status !== 'uploading') writtenUriRef.current.delete(entry.id)
      }
      uploadBudgetRef.current = {files: 0, bytes: 0}
      references?.clearPastedReferences(submittedReferences.map(item => item.token.id))
      // Only the rejections the user had already seen when they submitted; an attempt that landed
      // mid-submit keeps its banner.
      clearRejections(submittedRejections)
    }
    return {
      attachments: [
        ...submittedRepos.map(repositoryAttachment),
        ...submittedFiles.map(item => item.ahp),
        // Only completed writes: a failed or in-flight upload has no path on disk, so
        // handing its attachment to the agent would point at a file that isn't there.
        ...submittedUploads,
        ...submittedReferences.map(item => item.ahp),
        ...submittedImages.map(imageAttachment),
      ],
      repos: submittedRepos,
      images: submittedImages,
      files: submittedFiles,
      references: submittedReferences,
      clear,
    }
  }, [clearRejections, images, inlineImages, references, selectedRepos, snapshotRejections, tracked, uploadEntries])

  const removeImage = useCallback(
    (id: string) => {
      setImages(current => renumberPastedImages(current.filter(image => image.id !== id)))
      // A pasted image is uploaded under its own id, so dropping the chip has to drop
      // the upload too. Otherwise a completed write still contributes its `resource`
      // attachment and the agent receives a path the user removed.
      discardUpload(id)
    },
    [discardUpload],
  )

  const openFilePicker = useCallback(() => setFilePickerOpen(true), [])
  const closeFilePicker = useCallback(() => setFilePickerOpen(false), [])
  const openRepoPicker = useCallback(() => setRepoPickerOpen(true), [])
  const closeRepoPicker = useCallback(() => setRepoPickerOpen(false), [])
  const dismissRejections = useCallback(() => {
    clearRejections()
    focusComposer?.()
  }, [clearRejections, focusComposer])

  return useMemo(
    () => ({
      attachments,
      repoChips,
      trackedChips,
      pastedChips,
      tracked,
      pastedReferences: references?.pastedReferences ?? [],
      images,
      hasAttachments,
      skippedFiles,
      rejections,
      rejectionMessages,
      dismissRejections,
      isUploading,
      uploadsInFlight,
      handleFilesAdded,
      handleFileInputChange,
      handleFilePickerSelect,
      addTrackedAttachments,
      removeTrackedAttachmentsByPrefix,
      handleUpdateRepos,
      handleRemoveAttachment,
      handlePaste,
      reconcileFromText,
      openFilePicker,
      closeFilePicker,
      filePickerOpen,
      openRepoPicker,
      closeRepoPicker,
      repoPickerOpen,
      selectedRepos,
      previewImage,
      setPreviewImage,
      removeImage,
      snapshot,
    }),
    [
      addTrackedAttachments,
      attachments,
      closeFilePicker,
      closeRepoPicker,
      dismissRejections,
      filePickerOpen,
      handleFileInputChange,
      handleFilePickerSelect,
      handleFilesAdded,
      handlePaste,
      handleRemoveAttachment,
      handleUpdateRepos,
      hasAttachments,
      images,
      isUploading,
      uploadsInFlight,
      openFilePicker,
      openRepoPicker,
      pastedChips,
      previewImage,
      reconcileFromText,
      references,
      rejectionMessages,
      rejections,
      removeImage,
      removeTrackedAttachmentsByPrefix,
      repoChips,
      repoPickerOpen,
      selectedRepos,
      setPreviewImage,
      skippedFiles,
      snapshot,
      tracked,
      trackedChips,
    ],
  )
}
